import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateLineItemRequestSchema } from '@weft/api-definition';
import type { FixedLineItem, FixedLineItemInput } from '@weft/types';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { useUpdateLineItem } from 'apps/app/src/hooks/requests/use-update-line-item';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { buildDefaultEditFixedLineItemFormValues } from './edit-fixed-line-item-dialog-form-utils/build-default-edit-fixed-line-item-form-values';
import { EditFixedLineItemDialogFromContent } from './EditFixedLineItemDialogFormContent';

type EditFixedLineItemDialogFormProps = {
  open: boolean;
  handleClose: () => void;
  invoiceId: string;
  customerId: string
  lineItem: FixedLineItem;
};

export const EditFixedLineItemDialogForm: React.FC<EditFixedLineItemDialogFormProps> = ({ open, handleClose, lineItem, invoiceId, customerId }) => {
  const queryClient = useQueryClient();
  const updateLineItemMutation = useUpdateLineItem();

  const methods = useForm<FixedLineItemInput>({
    resolver: zodResolver(UpdateLineItemRequestSchema),
    defaultValues: buildDefaultEditFixedLineItemFormValues(lineItem),
  });

  useEffect(() => {
    methods.reset(buildDefaultEditFixedLineItemFormValues(lineItem));
  }, [lineItem]);

  const onSubmit = methods.handleSubmit(async (data) => {
    await runMutation(
      updateLineItemMutation, 
      {
        lineItemId: lineItem.id,
        updateLineItemRequest: {
          ...data,
          revisionedLineItem: undefined,
        },
      }, {
        pending: 'Updating line item...',
        success: 'Line item updated',
        error: 'Failed to update line item',
      }, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: query => [invoiceId, customerId].some(key => query.queryKey.includes(key) && query.queryKey.includes('dynamicInvoice')),
          });
        },
      
      });

    handleClose();
  });

  return (
    <VyDialog  
      open={open}
      onClose={handleClose}
      label="Update line item"
      labelDescription="This will terminate the contract and scheduled invoices"
      cancelButtonText="Cancel"
      ctaText="Update"
      ctaDisabled={updateLineItemMutation.isLoading}
      onSubmit={onSubmit}
    >
      <FormProvider {...methods}>
        <form >
          <EditFixedLineItemDialogFromContent />
        </form>
      </FormProvider>
    </VyDialog>
  );
};